// Only Atlas' per-line stock contract is consumed here. No Ada-quote envelope or aliases.
const has = (o, k) => Object.hasOwn(o, k);
const number = value => typeof value === "number" && Number.isFinite(value) ? value : null;
const text = value => typeof value === "string" && value.trim() ? value.trim() : null;
const orderNumber = value => typeof value === "number" ? number(value) : text(value);

/** Optional authorized Atlas DTO. Raw Ada-quote envelopes are never interpreted. */
export function stockCustomerOrders(stock) {
  if (stock?.status !== "ok" || stock.reason === "stale" || stock.indicator !== "red" ||
      !["customer_orders", "customer_orders_count"].some(key => has(stock, key))) return null;
  return {
    count: Number.isSafeInteger(stock.customer_orders_count) && stock.customer_orders_count >= 0 ? stock.customer_orders_count : null,
    orders: Array.isArray(stock.customer_orders) ? stock.customer_orders
      .filter(row => row && typeof row === "object" && !Array.isArray(row)).map(row => ({
        number: orderNumber(row.order_number), year: Number.isInteger(row.year) ? row.year : null,
        quantity: number(row.quantity), orderDate: text(row.order_date),
        deliveryDate: text(row.delivery_date), formattedDeliveryDate: text(row.formatted_delivery_date),
        reportedDate: text(row.formatted_date) ?? text(row.date),
        customerCode: text(row.customer_code), customerName: text(row.customer_name),
        customerOrderNumber: text(row.customer_order_number),
      })) : null,
  };
}

/** Arrivals are calendar days, including when the producer encodes midnight as ISO. */
export function stockArrivalDate(value, locale = "es-ES") {
  if (typeof value !== "string") return null;
  const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:$|T)/);
  const formatted = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!iso && !formatted) return null;
  const [year, month, day] = iso ? iso.slice(1).map(Number)
    : [Number(formatted[3]), Number(formatted[2]), Number(formatted[1])];
  const date = new Date(Date.UTC(year, month - 1, day, 12));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;
  return new Intl.DateTimeFormat(locale, { timeZone: "UTC", dateStyle: "short" }).format(date);
}

export function stockSupply(stock) {
  if (stock?.status !== "ok" || stock.reason === "stale") return null;
  const metrics = [["pendingCustomers", "customer_pending"], ["supplierOrders", "supplier_pending"]]
    .flatMap(([key, field]) => has(stock, field) ? [{ key, value: number(stock[field]) }] : []);
  const warehousePresent = has(stock, "warehouses") || has(stock, "physical_warehouses");
  let warehouses = null;
  if (has(stock, "warehouses")) {
    if (Array.isArray(stock.warehouses)) warehouses = stock.warehouses
      .filter(row => row && typeof row === "object" && !Array.isArray(row))
      .map(row => ({ id: text(row.id), name: text(row.name) ?? text(row.id), quantity: number(row.physical), unit: text(row.unit) }))
      .filter(row => row.name);
  } else if (stock.physical_warehouses && typeof stock.physical_warehouses === "object" && !Array.isArray(stock.physical_warehouses)) {
    warehouses = Object.entries(stock.physical_warehouses).map(([id, quantity]) => ({ id, name: id, quantity: number(quantity), unit: null }));
  }
  const orders = Array.isArray(stock.supplier_orders) ? stock.supplier_orders
    .filter(row => row && typeof row === "object" && !Array.isArray(row)).map(row => ({
      number: orderNumber(row.order_number), year: Number.isInteger(row.year) ? row.year : null,
      quantity: number(row.quantity), arrival: has(row, "arrival_date") ? row.arrival_date : row.formatted_arrival_date ?? null,
      text: null,
    })) : null;
  const arrivalRows = Array.isArray(stock.arrival_dates) ? stock.arrival_dates
    .filter(row => row && typeof row === "object" && !Array.isArray(row)).map(row => ({
      number: null, year: null, quantity: number(row.quantity), arrival: row.date ?? null, text: text(row.text),
    })) : null;
  // Orders are the detailed representation of their dates. Keep every order,
  // including repeated dates; append only additional calendar days from the summary.
  const orderDays = new Set((orders ?? []).map(row => stockArrivalDate(row.arrival)).filter(Boolean));
  const arrivals = orders?.length
    ? [...orders, ...(arrivalRows ?? []).filter(row => !orderDays.has(stockArrivalDate(row.arrival)))]
    : arrivalRows?.length ? arrivalRows : orders ?? arrivalRows;
  const countPresent = has(stock, "supplier_orders_count");
  const multiplePresent = has(stock, "has_multiple_supplier_orders");
  const nextArrivalPresent = has(stock, "next_arrival_date");
  const message = text(stock.message), level = text(stock.level);
  const breakdown = !arrivals?.length ? text(stock.arrival_breakdown) : null;
  const ordersPresent = has(stock, "supplier_orders") || has(stock, "arrival_dates") || has(stock, "arrival_breakdown");
  if (!metrics.length && !warehousePresent && !ordersPresent && !countPresent && !multiplePresent && !nextArrivalPresent && !message && !level) return null;
  return { metrics, warehouses, warehousePresent, orders: arrivals, ordersPresent,
    count: Number.isSafeInteger(stock.supplier_orders_count) && stock.supplier_orders_count >= 0 ? stock.supplier_orders_count : null, countPresent,
    hasMultiple: typeof stock.has_multiple_supplier_orders === "boolean" ? stock.has_multiple_supplier_orders : null, multiplePresent,
    nextArrival: stock.next_arrival_date, nextArrivalPresent, breakdown, message, level };
}

export function stockFields(stock) {
  return [["physical", "stock.physical"], ["virtual", "stock.virtual"],
    ["committed", "stock.committedColumn"], ["available", "stock.availableColumn"],
    ...(has(stock, "remaining") ? [["remaining", "stock.remainingAfterRequest"]] : [])];
}

export function stockReasonKey(reason) {
  return ({ stale: "stock.stale", not_checked: "stock.not_queried", stock_unit_unverified: "stock.notComparable",
    purchase_basis_unknown: "stock.purchaseBasisUnknown", identity_unverified: "stock.identityUnverified",
    upstream_unavailable: "stock.sourceUnavailable", stock_check_unavailable: "stock.sourceUnavailable",
    stock_check_timeout: "stock.sourceUnavailable", master_version_unknown: "stock.identityUnverified" })[reason] ?? "stock.reasonUnknown";
}
